import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { postMethod } from "../../../requests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";

import UserRolesDropdown from "../../../components/dropdowns/UserRolesDropdown";
import Search from "../../../components/inputs/Search";
import { BasicContext } from "../../../App";
import LoadedRecords from "../../../components/LoadedRecords";
import UserStatusesDropdown from "../../../components/dropdowns/UserStatusesDropdown";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import UsersListXS from "../../../components/user/UsersListXS";
import UsersListLG from "../../../components/user/UsersListLG";

const UsersList = () => {
    const { t } = useTranslation();
    const { checkServerData, changeView, NOR, setAlerts } = useContext(BasicContext);
    const [list, setList] = useState(null);
    const [originalList, setOriginalList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedUserRoles, setSelectedUserRoles] = useState([]);
    const [selectedUserStatus, setSelectedUserStatus] = useState("default");
    const [search, setSearch] = useState("");
    const [displayNOR, setDisplayNOR] = useState(NOR);
    const [showChangeStudentStatusModal, setShowChangeStudentStatusModal] = useState(false);
    const [privilleges, setPrivilleges] = useState(null);

    const getUsersList = async () => {
        const res = await postMethod("/users-list");
        if (checkServerData(res)) {
            setPrivilleges(res.data.addNewUserPriv);
            setList(res.data.list);
            setOriginalList(res.data.list);
        }
    };

    const acceptNewStudent = async (userID) => {
        const res = await postMethod("/accept-new-student", { userID: userID });
        if (checkServerData(res)) {
            setShowChangeStudentStatusModal(false);
            setAlerts([{ variant: "success", message: t(`success.${res.data.code}`), fading: 1500, id: "acceptNewStudent" }]);
            setLoading(true);
            getUsersList();
        }
    };

    const handleSearch = (v) => {
        const searchedPhrase = v.toLowerCase();
        setSearch(searchedPhrase);
        if (searchedPhrase !== "") {
            let filterThisList = originalList;
            filterThisList = filterThisList.filter(
                (el) =>
                    (el.user_name.toString().toLowerCase() + " " + el.user_surname.toString().toLowerCase()).includes(searchedPhrase) ||
                    el.user_phone.toString().toLowerCase().includes(searchedPhrase) ||
                    el.user_email.toString().toLowerCase().includes(searchedPhrase) ||
                    (el.user_instagram && el.user_instagram.toString().toLowerCase().includes(searchedPhrase)) //el.user_instagram może być nullem
            );
            setList(filterThisList);
        } else {
            setList(originalList);
        }
    };

    const filterList = (e) => {
        const searchForRoles = (rolesList) => {
            let ifUserHasRole = false;
            selectedUserRoles.forEach((r) => {
                rolesList.forEach((e) => {
                    if (r === e) ifUserHasRole = true;
                });
            });
            return ifUserHasRole;
        };

        if ((selectedUserRoles.length === 0 || searchForRoles(e.user_role_code_list)) && (selectedUserStatus === "default" || selectedUserStatus === e.user_stat_code)) return true;
        else return false;
    };

    useEffect(() => {
        if (list) setLoading(false);
        // eslint-disable-next-line
    }, [list]);

    const checkIfUserNeedsAttention = (e) => {
        const today = new Date(),
            lastLogin = new Date(e.last_login_date);
        const monthDifference = (today.getFullYear() - lastLogin.getFullYear()) * 12 + (today.getMonth() - lastLogin.getMonth());

        const attentionList = [];
        if (!e.user_terms_agreement || e.user_terms_agreement === "N") attentionList.push(t("error.notCheckedRegulations"));
        if (!e.user_rodo_agreement || e.user_rodo_agreement === "N") attentionList.push(t("error.notCheckedRodo"));
        if (!e.last_login_date) attentionList.push(t("warning.userNeverLoggedIn"));
        else if (monthDifference > 3 || (monthDifference === 3 && today.getDate() >= lastLogin.getDate())) attentionList.push(t("warning.userInactiveForLongTime"));

        return attentionList;
    };

    const checkIfUserIsNewStudent = (userRoles) => {
        let userIsNewStudent = false;
        userRoles.forEach((ur) => {
            if (ur === "N_STUDENT") userIsNewStudent = true;
        });
        return userIsNewStudent;
    };

    useEffect(() => {
        getUsersList();

        // eslint-disable-next-line
    }, []);

    return (
        <Fragment>
            {loading ? (
                <Container>
                    <Row>
                        <Col className="my-5 text-center">
                            <p className="h4">{t("loadingData")}</p>
                        </Col>
                    </Row>
                </Container>
            ) : (
                <Fragment>
                    <Container fluid="lg">
                        <Row>
                            <Col xs={12} className="px-4 my-4 px-lg-2 ">
                                <h1 className="h4 d-inline-block m-0 pt-2">{t("users.users")}</h1>
                                {privilleges && (
                                    <Button className="float-end d-inline-block" variant="outline-primary" onClick={() => changeView({ nav: "users", sub: "addUser" })}>
                                        <FontAwesomeIcon icon={faUserPlus} className="me-lg-2" />
                                        <span className="d-none d-lg-inline">{t("users.addUser")}</span>
                                    </Button>
                                )}
                                <Button
                                    className="float-end d-inline-block me-2"
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setLoading(true);
                                        getUsersList();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faArrowsRotate} />
                                </Button>
                            </Col>
                        </Row>
                        <Row className="sticky-top bg-light-primary">
                            <Col xs={12} lg={6} className="px-4 my-lg-4 px-lg-2">
                                <UserRolesDropdown onChange={(v) => setSelectedUserRoles(v)} value={selectedUserRoles} multiple />
                            </Col>
                            <Col xs={12} lg={6} className="px-4 my-lg-4 my-3 px-lg-2">
                                <UserStatusesDropdown onChange={(v) => setSelectedUserStatus(v)} value={selectedUserStatus} allOption />
                            </Col>
                            <Col xs={12} lg={6} className="px-4 my-lg-4 mb-3 px-lg-2 ">
                                <Search value={search} onChange={(e) => handleSearch(e.target.value)} />
                            </Col>
                        </Row>
                        <Row>
                            {list.filter(filterList).length > 0 ? (
                                <Fragment>
                                    <LoadedRecords displayNOR={displayNOR} list={list.filter(filterList)} />
                                    <Col xs={12} className="px-0 px-lg-2 ">
                                        <Table className="white-table d-lg-none">
                                            <tbody>
                                                {list
                                                    .filter(filterList)
                                                    .map((e, i) => (
                                                        <Fragment key={i}>
                                                            <UsersListXS
                                                                checkIfUserNeedsAttention={checkIfUserNeedsAttention}
                                                                e={e}
                                                                i={i}
                                                                checkIfUserIsNewStudent={checkIfUserIsNewStudent}
                                                                onShow={setShowChangeStudentStatusModal}
                                                                changeView={changeView}
                                                            />
                                                        </Fragment>
                                                    ))
                                                    .slice(0, displayNOR)}
                                            </tbody>
                                        </Table>
                                        <Table className="white-table d-none d-lg-table">
                                            <tbody>
                                                {list
                                                    .filter(filterList)
                                                    .map((e, i) => (
                                                        <Fragment key={i}>
                                                            <UsersListLG
                                                                checkIfUserNeedsAttention={checkIfUserNeedsAttention}
                                                                e={e}
                                                                i={i}
                                                                checkIfUserIsNewStudent={checkIfUserIsNewStudent}
                                                                onShow={setShowChangeStudentStatusModal}
                                                                changeView={changeView}
                                                            />
                                                        </Fragment>
                                                    ))
                                                    .slice(0, displayNOR)}
                                            </tbody>
                                        </Table>
                                    </Col>
                                    <LoadedRecords displayNOR={displayNOR} list={list.filter(filterList)} />
                                    <Col xs={12} className="text-center">
                                        {displayNOR >= list.filter(filterList).length ? (
                                            <p className="opacity-4  mb-3">
                                                <span className="px-3">{t("thatsAll")}</span>
                                            </p>
                                        ) : (
                                            <Button onClick={() => setDisplayNOR(NOR + displayNOR)} variant="outline-primary" className="mb-3">
                                                {t("loadMore")}
                                            </Button>
                                        )}
                                    </Col>
                                </Fragment>
                            ) : (
                                <Col xs={12} className="text-center">
                                    <p className="fs-5">{t("notFound")}</p>
                                </Col>
                            )}
                        </Row>
                    </Container>
                </Fragment>
            )}

            {showChangeStudentStatusModal && (
                <ConfirmModal
                    show={true}
                    onClose={() => {
                        setAlerts([]);
                        setShowChangeStudentStatusModal(false);
                    }}
                    onProceed={() => {
                        setAlerts([{ variant: "info", message: t("info.changingRoles") }]);
                        setTimeout(() => {
                            acceptNewStudent(showChangeStudentStatusModal);
                        }, 150);
                    }}
                    text={t("acceptNewStudentModal.text")}
                    proceedTxt={t("acceptNewStudentModal.proceedTxt")}
                    cancelTxt={t("acceptNewStudentModal.cancelTxt")}
                    title={t("acceptNewStudentModal.title")}
                />
            )}
        </Fragment>
    );
};

export default UsersList;
